import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { doc, updateDoc, deleteDoc, getDoc, collection, query, where, getDocs, onSnapshot, deleteField, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { copyToClipboard } from '../../utils/clipboard';
import { DEFAULT_SHARE_TEMPLATE } from '../../utils/whatsapp';
import './CalendarSettings.css';

// Map variable codes to friendly display labels
const TEMPLATE_VARS = [
  { varCode: '{dayName}',       display: '[ اليوم ]',           label: 'اليوم' },
  { varCode: '{gregorianDate}', display: '[ التاريخ الميلادي ]', label: 'التاريخ الميلادي' },
  { varCode: '{hijriDate}',     display: '[ التاريخ الهجري ]',   label: 'التاريخ الهجري' },
  { varCode: '{note}',          display: '[ المناسبة ]',          label: 'المناسبة' },
];

// Convert raw template string → HTML with styled var chips
function rawToHtml(raw) {
  let html = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  TEMPLATE_VARS.forEach(({ varCode, display }) => {
    const escaped = varCode.replace(/[{}]/g, '\\$&');
    html = html.replace(
      new RegExp(escaped, 'g'),
      `<span class="tpl-var-chip" data-var="${varCode}" contenteditable="false">${display}</span>`
    );
  });

  html = html.replace(/\n/g, '<br>');
  return html;
}

// Convert contentEditable DOM → raw template string
function htmlToRaw(el) {
  let text = '';

  function walk(node) {
    if (node.nodeType === 3) { text += node.textContent; return; }
    if (node.nodeType !== 1) return;
    const tag = node.tagName;
    if (tag === 'SPAN' && node.dataset.var) { text += node.dataset.var; return; }
    if (tag === 'BR') { text += '\n'; return; }
    if (tag === 'DIV' || tag === 'P') {
      if (text && !text.endsWith('\n')) text += '\n';
      node.childNodes.forEach(walk);
      return;
    }
    node.childNodes.forEach(walk);
  }

  el.childNodes.forEach(walk);
  return text.replace(/\n+$/, '');
}

export default function CalendarSettingsModal({ calendar, onClose, onUpdated, isAdmin = false, onLeave }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast, confirm } = useToast();
  const [calendarName, setCalendarName] = useState(calendar.name);
  const [shareTemplate, setShareTemplate] = useState(calendar.shareTemplate || DEFAULT_SHARE_TEMPLATE);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cellSize, setCellSize] = useState(() => localStorage.getItem('calendarCellSize') || 'normal');

  function handleCellSizeChange(size) {
    setCellSize(size);
    localStorage.setItem('calendarCellSize', size);
    window.dispatchEvent(new CustomEvent('calendarCellSizeChange', { detail: size }));
  }

  // Smart template editor refs
  const editorRef = useRef(null);
  const lastRenderedRef = useRef(null);

  // Initialize editor DOM on mount
  useLayoutEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = rawToHtml(shareTemplate);
      lastRenderedRef.current = shareTemplate;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update editor DOM when shareTemplate changes from outside (reset / external update)
  useEffect(() => {
    if (!editorRef.current) return;
    if (shareTemplate === lastRenderedRef.current) return; // Skip internal (user-typing) changes
    editorRef.current.innerHTML = rawToHtml(shareTemplate);
    lastRenderedRef.current = shareTemplate;
  }, [shareTemplate]);

  function handleEditorInput() {
    const raw = htmlToRaw(editorRef.current);
    lastRenderedRef.current = raw;
    setShareTemplate(raw);
  }

  function handleKeyDown(e) {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (!range.collapsed) return; // Let browser handle selection deletion

    const { startContainer, startOffset } = range;
    const editor = editorRef.current;
    let chipToRemove = null;

    if (e.key === 'Backspace') {
      if (startContainer === editor) {
        const prev = editor.childNodes[startOffset - 1];
        if (prev && prev.classList && prev.classList.contains('tpl-var-chip')) chipToRemove = prev;
      } else if (startContainer.nodeType === 3 && startOffset === 0) {
        const prev = startContainer.previousSibling;
        if (prev && prev.classList && prev.classList.contains('tpl-var-chip')) chipToRemove = prev;
      }
    } else {
      if (startContainer === editor) {
        const next = editor.childNodes[startOffset];
        if (next && next.classList && next.classList.contains('tpl-var-chip')) chipToRemove = next;
      } else if (startContainer.nodeType === 3 && startOffset === startContainer.textContent.length) {
        const next = startContainer.nextSibling;
        if (next && next.classList && next.classList.contains('tpl-var-chip')) chipToRemove = next;
      }
    }

    if (chipToRemove) {
      e.preventDefault();
      const newRange = document.createRange();
      newRange.setStartBefore(chipToRemove);
      newRange.collapse(true);
      chipToRemove.remove();
      sel.removeAllRanges();
      sel.addRange(newRange);
      handleEditorInput();
    }
  }

  function insertVariable(varCode, display) {
    const editor = editorRef.current;
    if (!editor || loading) return;
    editor.focus();

    const chip = document.createElement('span');
    chip.className = 'tpl-var-chip';
    chip.setAttribute('data-var', varCode);
    chip.setAttribute('contenteditable', 'false');
    chip.textContent = display;

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editor.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(chip);
      range.setStartAfter(chip);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editor.appendChild(chip);
    }

    handleEditorInput();
  }

  // Fetch member details with real-time updates
  useEffect(() => {
    // Listen for changes to the calendar document
    const unsubscribe = onSnapshot(
      doc(db, 'calendars', calendar.id),
      async (calendarDoc) => {
        if (!calendarDoc.exists()) return;

        const calendarData = calendarDoc.data();
        const memberUids = calendarData.members || [];

        // Fetch user documents for each UID in calendar.members directly
        const memberDocs = await Promise.all(
          memberUids.map(uid => getDoc(doc(db, 'users', uid)))
        );
        const membersList = memberDocs
          .filter(d => d.exists())
          .map(d => ({ id: d.id, ...d.data() }));

        setMembers(membersList);
      }
    );

    // Clean up listener on unmount
    return () => unsubscribe();
  }, [calendar.id]);

  async function handleUpdateName() {
    if (!calendarName.trim() || calendarName === calendar.name) return;

    try {
      setLoading(true);
      await updateDoc(doc(db, 'calendars', calendar.id), {
        name: calendarName.trim()
      });
      onUpdated();
      showToast('تم تحديث اسم التقويم بنجاح!', 'success');
    } catch (error) {
      console.error('Error updating calendar:', error);
      showToast('حدث خطأ أثناء التحديث', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateShareTemplate() {
    const trimmed = shareTemplate.trim();
    if (!trimmed || trimmed === (calendar.shareTemplate || DEFAULT_SHARE_TEMPLATE)) return;

    try {
      setLoading(true);
      await updateDoc(doc(db, 'calendars', calendar.id), {
        shareTemplate: trimmed
      });
      showToast('تم حفظ رسالة المشاركة بنجاح!', 'success');
    } catch (error) {
      console.error('Error updating share template:', error);
      showToast('حدث خطأ أثناء الحفظ', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function copyInviteCode() {
    const success = await copyToClipboard(calendar.inviteCode);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      showToast('فشل النسخ. حاول مرة أخرى.', 'error');
    }
  }

  async function handleRemoveMember(memberId) {
    if (memberId === calendar.createdBy) {
      showToast('لا يمكن إزالة منشئ التقويم', 'warning');
      return;
    }

    const confirmed = await confirm('هل تريد إزالة هذا العضو من التقويم؟');
    if (!confirmed) return;

    try {
      setLoading(true);

      // Remove member from calendar atomically
      await updateDoc(doc(db, 'calendars', calendar.id), {
        members: arrayRemove(memberId)
      });

      // Clean up the removed user's document
      const userDocRef = doc(db, 'users', memberId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const updatedCalendars = (userData.calendars || []).filter(calId => calId !== calendar.id);
        await updateDoc(userDocRef, {
          calendars: updatedCalendars,
          [`lastAccessed.${calendar.id}`]: deleteField()
        });
      }

      showToast('تم إزالة العضو بنجاح', 'success');
    } catch (error) {
      console.error('Error removing member:', error);
      showToast('حدث خطأ أثناء إزالة العضو', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleLeaveCalendar() {
    if (!currentUser) return;

    const confirmed = await confirm('هل تريد مغادرة هذا التقويم؟ لن تتمكن من الوصول إليه إلا إذا تمت دعوتك مرة أخرى.');
    if (!confirmed) return;

    let navigated = false;
    try {
      setLoading(true);
      if (onLeave) onLeave();

      // Remove user from calendar members — wait for server confirmation
      await updateDoc(doc(db, 'calendars', calendar.id), {
        members: arrayRemove(currentUser.uid)
      });

      // Navigate immediately once membership removal is server-confirmed
      // so CalendarList mounts with up-to-date Firestore cache
      navigated = true;
      showToast('تم مغادرة التقويم بنجاح', 'success');
      navigate('/dashboard');

      // Cleanup user document in background (component may unmount)
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const updatedUserCalendars = (userData.calendars || []).filter(calId => calId !== calendar.id);
        await updateDoc(doc(db, 'users', currentUser.uid), {
          calendars: updatedUserCalendars,
          [`lastAccessed.${calendar.id}`]: deleteField()
        });
      }
    } catch (error) {
      console.error('Error leaving calendar:', error);
      if (!navigated) {
        showToast('حدث خطأ أثناء مغادرة التقويم', 'error');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteCalendar() {
    try {
      setLoading(true);

      console.log('Starting calendar deletion...');
      console.log('Calendar ID:', calendar.id);
      console.log('Calendar creator:', calendar.createdBy);

      // Remove calendar from all members' calendars arrays, lastAccessed, and favoriteCalendars
      console.log('Removing calendar from members calendars arrays, lastAccessed, and favoriteCalendars...');
      for (const memberId of calendar.members || []) {
        try {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const updatedUserCalendars = (userData.calendars || []).filter(calId => calId !== calendar.id);
            const updatedFavorites = (userData.favoriteCalendars || []).filter(calId => calId !== calendar.id);
            await updateDoc(doc(db, 'users', memberId), {
              calendars: updatedUserCalendars,
              favoriteCalendars: updatedFavorites,
              [`lastAccessed.${calendar.id}`]: deleteField()
            });
          }
        } catch (error) {
          console.error(`Error updating user ${memberId}:`, error);
        }
      }
      console.log('Removed calendar from all members');

      // Also remove from favoriteCalendars of any non-member users who might have favorited it
      console.log('Cleaning up favoriteCalendars from all users...');
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.favoriteCalendars && userData.favoriteCalendars.includes(calendar.id)) {
          const updatedFavorites = userData.favoriteCalendars.filter(calId => calId !== calendar.id);
          await updateDoc(doc(db, 'users', userDoc.id), {
            favoriteCalendars: updatedFavorites
          });
          console.log(`Removed from favorites of user ${userDoc.id}`);
        }
      }
      console.log('Cleaned up all favoriteCalendars');

      // Delete all bookings for this calendar
      console.log('Fetching bookings to delete...');
      const bookingsRef = collection(db, 'bookings');
      const q = query(bookingsRef, where('calendarId', '==', calendar.id));
      const bookingsSnapshot = await getDocs(q);

      console.log('Found', bookingsSnapshot.size, 'bookings to delete');

      if (bookingsSnapshot.size > 0) {
        const deletePromises = bookingsSnapshot.docs.map(bookingDoc =>
          deleteDoc(doc(db, 'bookings', bookingDoc.id))
        );
        await Promise.all(deletePromises);
        console.log('All bookings deleted successfully');
      }

      // Delete the calendar
      console.log('Attempting to delete calendar document...');
      await deleteDoc(doc(db, 'calendars', calendar.id));
      console.log('Calendar deleted successfully');

      showToast('تم حذف التقويم بنجاح', 'success');
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Error deleting calendar:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      showToast('حدث خطأ أثناء حذف التقويم', 'error');
      setLoading(false);
    }
  }

  if (showDeleteConfirm) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>تأكيد الحذف</h2>
            <button className="close-btn" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="confirm-delete-content">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <h3>هل أنت متأكد؟</h3>
            <p>سيتم حذف التقويم "{calendar.name}" وجميع الحجوزات بشكل نهائي. لا يمكن التراجع عن هذا الإجراء.</p>
          </div>

          <div className="modal-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteCalendar}
              disabled={loading}
            >
              {loading ? 'جاري الحذف...' : 'حذف التقويم نهائياً'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isAdmin ? 'إعدادات التقويم' : 'أعضاء التقويم'}</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Calendar Name - Admin only */}
        {isAdmin && (
          <div className="settings-section">
            <label className="section-label">اسم التقويم</label>
            <div className="name-edit-group">
              <input
                type="text"
                value={calendarName}
                onChange={(e) => setCalendarName(e.target.value)}
                maxLength={50}
                disabled={loading}
              />
              <button
                className="btn btn-primary btn-sm"
                onClick={handleUpdateName}
                disabled={loading || !calendarName.trim() || calendarName === calendar.name}
              >
                حفظ
              </button>
            </div>
          </div>
        )}

        {/* Calendar Name - Read only for members */}
        {!isAdmin && (
          <div className="settings-section">
            <label className="section-label">اسم التقويم</label>
            <div className="calendar-name-readonly">{calendar.name}</div>
          </div>
        )}

        {/* WhatsApp Share Template - Admin only */}
        {isAdmin && (
          <div className="settings-section">
            <label className="section-label">رسالة مشاركة واتساب</label>
            <p className="share-template-hint">انقر على متغير لإدراجه في موضع المؤشر</p>
            <div className="template-var-chips">
              {TEMPLATE_VARS.map(({ varCode, display, label }) => (
                <button
                  key={varCode}
                  type="button"
                  className="template-var-chip"
                  onClick={() => insertVariable(varCode, display)}
                  disabled={loading}
                >
                  {label}
                </button>
              ))}
            </div>
            <div
              ref={editorRef}
              className={`smart-template-editor${loading ? ' disabled' : ''}`}
              contentEditable={!loading}
              suppressContentEditableWarning={true}
              onInput={handleEditorInput}
              onKeyDown={handleKeyDown}
              onPaste={(e) => {
                e.preventDefault();
                const text = (e.clipboardData || window.clipboardData).getData('text/plain');
                document.execCommand('insertText', false, text);
              }}
              dir="rtl"
              spellCheck="false"
            />
            <div className="template-actions">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShareTemplate(DEFAULT_SHARE_TEMPLATE)}
                disabled={loading || shareTemplate === DEFAULT_SHARE_TEMPLATE}
              >
                إعادة تعيين
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleUpdateShareTemplate}
                disabled={loading || !shareTemplate.trim() || shareTemplate.trim() === (calendar.shareTemplate || DEFAULT_SHARE_TEMPLATE)}
              >
                حفظ
              </button>
            </div>
          </div>
        )}

        {/* Invite Code - admin only */}
        {isAdmin && (
          <div className="settings-section">
            <label className="section-label">رمز الدعوة</label>
            <div className="code-display-settings">
              <span className="invite-code-text">{calendar.inviteCode}</span>
              <button className="btn-copy-small" onClick={copyInviteCode}>
                {copied ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                )}
              </button>
            </div>
            {copied && <span className="copied-text">تم النسخ! ✓</span>}
          </div>
        )}

        {/* Cell Size Preference */}
        <div className="settings-section mobile-only-section">
          <label className="section-label">حجم الخلايا</label>
          <div className="cell-size-toggle">
            <button
              className={`cell-size-btn ${cellSize === 'normal' ? 'active' : ''}`}
              onClick={() => handleCellSizeChange('normal')}
            >
              عادي
            </button>
            <button
              className={`cell-size-btn ${cellSize === 'large' ? 'active' : ''}`}
              onClick={() => handleCellSizeChange('large')}
            >
              كبير
            </button>
          </div>
        </div>

        {/* Members List */}
        <div className="settings-section">
          <label className="section-label">الأعضاء ({members.length})</label>
          <div className="members-list">
            {members.map(member => {
              const isCreator = member.id === calendar.createdBy;
              const canManage = !isCreator; // Can't manage the creator

              return (
                <div key={member.id} className="member-item">
                  <div className="member-avatar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div className="member-info">
                    <span className="member-name">{member.displayName}</span>
                    <span className="member-email">{member.email}</span>
                  </div>

                  <div className="member-badges">
                    {isCreator && (
                      <span className="admin-badge-small creator-badge">المنشئ</span>
                    )}
                  </div>

                  {isAdmin && canManage && (
                    <div className="member-actions">
                      <button
                        className="action-btn remove-btn"
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={loading}
                        title="إزالة من المجموعة"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Leave Calendar - Members only (not creator) */}
        {currentUser && currentUser.uid !== calendar.createdBy && (
          <div className="settings-section leave-section">
            <button
              className="btn btn-leave btn-block"
              onClick={handleLeaveCalendar}
              disabled={loading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              مغادرة التقويم
            </button>
          </div>
        )}

        {/* Danger Zone - Admin only */}
        {isAdmin && (
          <div className="settings-section danger-zone">
            <label className="section-label">الخروج من التقويم</label>
            <button
              className="btn btn-danger btn-block"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              حذف التقويم نهائياً
            </button>
          </div>
        )}
      </div>
    </div>
  );
}